import React from "react"
import PropTypes from "prop-types"
import FontAwesome from "react-fontawesome"
import Fuse from "fuse.js"
import moment from "moment"
import CSVButton from "@swipesense/csv-button"

import { latestUpdatedTime } from "../../helpers/searchAssets"
import { fuseConfig } from "../../helpers/sharedHelperFunctions"

import ResultCount from "../ResultCount"
import Loading from "../Loading"
import DepartmentDropdownWithData from "../DepartmentDropdown/DepartmentDropdownWithData"
import UnitDropdownWithData from "../UnitDropdown/UnitDropdownWithData"
import SearchBox from "../SearchBox"

import AllAssetsTableVirtualized from "./AllAssetsTableVirtualized"
import "./AllAssetsReport.scss"

const sortAllAssets = (assetA, assetB) => {
  /* istanbul ignore next */
  if (assetA.item.assetTag.createdAt < assetB.item.assetTag.createdAt) {
    return -1
  }
  /* istanbul ignore next */
  if (assetA.item.assetTag.createdAt > assetB.item.assetTag.createdAt) {
    return 1
  }
  return 0
}

const searchAllAssets = (searchTerms, assets) => {
  if (!searchTerms) {
    return assets.map(asset => ({
      item: asset,
    }))
  }
  const fuse = new Fuse(assets, {
    ...fuseConfig,
    keys: [
      "name",
      "assetId",
      "labels",
      "manufacturer",
      "modelName",
      "location.name",
    ],
  })
  return fuse.search(searchTerms)
}

const csvHeaders = [
  { label: "Asset", key: "name" },
  { label: "Manufacturer", key: "manufacturer" },
  { label: "Model", key: "modelName" },
  { label: "Asset ID", key: "assetId" },
  { label: "Serial Number", key: "serialNumber" },
  { label: "Department", key: "ownerNames" },
  { label: "Ownership", key: "ownership" },
  { label: "Last Location", key: "locationName" },
  { label: "Last Seen", key: "timeLost" },
  { label: "Tag Assigned", key: "tagAssigned" },
  { label: "Battery Status", key: "batteryStatus" },
]

const csvDataMap = asset => {
  /* istanbul ignore next */
  const unitName =
    asset.item.location && asset.item.location.unit
      ? asset.item.location.unit.name
      : ""
  /* istanbul ignore next */
  const locationName = asset.item.location ? asset.item.location.name : ""
  /* istanbul ignore next */
  const tagAssigned = asset.item.assetTag
    ? moment(asset.item.assetTag.createdAt).format("HH:MM MM/DD/YYYY")
    : ""

  let timeLost
  /* istanbul ignore next */
  if (!asset.item.lastSeenAt) {
    timeLost = ""
  } else {
    timeLost = moment(asset.item.lastSeenAt).format("HH:MM MM/DD/YYYY")
  }

  return {
    ...asset.item,
    locationName: [unitName, locationName].join(" "),
    ownerNames: asset.item.owners.map(owner => owner.name.trim()).join(" and "),
    timeLost,
    tagAssigned,
  }
}

class AllAssetsReport extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      filterSearch: "",
      filterToRentals: false,
      filteredDept: null,
      filteredUnit: null,
    }
  }

  updateDepartment = departmentSelection => {
    this.setState({
      filteredDept: departmentSelection,
    })
  }

  updateUnit = unitSelection => {
    this.setState({
      filteredUnit: unitSelection,
    })
  }

  render() {
    const formattedUpdateTime = this.props.loading
      ? null
      : latestUpdatedTime(this.props.allAssets)

    const availableAssets = this.props.allAssets
      .filter(
        asset => !this.state.filterToRentals || asset.ownership === "rented"
      )
      .filter(
        asset =>
          this.state.filteredDept == null ||
          this.state.filteredDept.value == null ||
          asset.owners.map(o => o.id).includes(this.state.filteredDept.value)
      )
      .filter(asset => {
        const unitId = asset.location ? asset.location.unit.id : ""
        return (
          this.state.filteredUnit == null ||
          this.state.filteredUnit.value == null ||
          unitId.includes(this.state.filteredUnit.value)
        )
      })
    const allAssets = searchAllAssets(
      this.state.filterSearch,
      availableAssets
    ).sort(sortAllAssets)

    const csvFileName = `SwipeSense All Asset Tag Report ${
      this.state.filteredDept ? this.state.filteredDept.label.trim() + " " : ""
    }${
      this.state.filteredUnit ? this.state.filteredUnit.label.trim() + " " : ""
    }${
      this.state.filterToRentals ? "Rentals " : ""
    }${new Date().toLocaleDateString()}.csv`

    return (
      <div className="all-assets-report-container">
        <div>
          {this.props.loading ? (
            <Loading />
          ) : (
            <div className="AllAssetsReport">
              <div className="all-assets-top-bar">
                <div className="all-assets-summary">
                  ALL TAGS
                  <div className="inventory-numbers">
                    <div className="inventory-percent">
                      <b>
                        {Math.round(
                          (allAssets.length / availableAssets.length) * 100
                        ) || 0}
                        %
                      </b>
                    </div>
                    <div className="inventory-fraction">
                      ({allAssets.length}/{availableAssets.length})
                    </div>
                  </div>
                </div>
                <div className="filter-options">
                  <label className="rentals-only" htmlFor="onlyRented">
                    <input
                      type="checkbox"
                      onChange={event => {
                        this.setState({
                          filterToRentals: event.target.checked,
                        })
                      }}
                      name={"onlyRented"}
                      id={"onlyRented"}
                      checked={this.state.filterToRentals}
                    />
                    <FontAwesome className="rental-tag" name={"tag"} />
                    Rentals only
                  </label>
                  <DepartmentDropdownWithData
                    className="department-dropdown"
                    facilityId={this.props.facilityId}
                    departmentUpdated={this.updateDepartment}
                  />
                  <UnitDropdownWithData
                    className="unit-dropdown"
                    facilityId={this.props.facilityId}
                    unitUpdated={this.updateUnit}
                  />
                </div>
              </div>
              <div className="report-controls">
                <ResultCount
                  resultCount={allAssets.length}
                  deviceType={this.props.deviceType}
                  loading={this.props.loading}
                  updateTime={formattedUpdateTime}
                />
                <div className="search-download">
                  <SearchBox
                    query={this.state.filterSearch}
                    onChange={query => this.setState({ filterSearch: query })}
                  />
                  <CSVButton
                    data={allAssets}
                    filename={csvFileName}
                    headers={csvHeaders}
                    mapData={csvDataMap}
                  />
                </div>
              </div>
              <div className="results">
                <AllAssetsTableVirtualized
                  allAssets={allAssets}
                  deviceType={this.props.deviceType}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    )
  }
}

AllAssetsReport.propTypes = {
  facilityId: PropTypes.string.isRequired,
  allAssets: PropTypes.array,
  loading: PropTypes.bool,
  deviceType: PropTypes.string.isRequired,
}

AllAssetsReport.defaultProps = {
  allAssets: [],
  deviceType: "desktop",
}

export default AllAssetsReport
